import { findVisibleAnchorRect } from '_shared_/js/utils/findVisibleAnchorRect.js';

// Text-column geometry of a Readium chapter document.
//
// ReadiumCSS lays paged chapters out as CSS multi-columns on `:root`, with the
// `body` centered inside each column and capped by the line-length max-width;
// in scroll mode the body is a single centered block. These helpers reproduce
// that geometry so margin indicators can dock next to the text edge instead of
// the iframe edge, which the text rarely reaches.

/**
 * Detects Readium's scroll mode, mirroring the ReadiumCSS selector
 * `:root[style*="readium-scroll-on"]`.
 * @param {HTMLElement} documentElement - The chapter document's root element
 * @returns {boolean}
 */
function isScrollModeDocument(documentElement) {
    return (documentElement.getAttribute('style') || '').includes('readium-scroll-on');
}

/**
 * Resolves the number of columns actually rendered, following the CSS
 * multi-column sizing rules for the `column-count`/`column-width` pair.
 * @param {CSSStyleDeclaration} rootStyle - Computed style of the root element
 * @param {number} availableWidth - Content-box width of the multicol container
 * @param {number} columnGap - Gap between columns in pixels
 * @returns {number}
 */
function resolveUsedColumnCount(rootStyle, availableWidth, columnGap) {
    const declaredCount = parseInt(rootStyle.columnCount, 10) || null;
    const declaredWidth = parseFloat(rootStyle.columnWidth) || null;
    if (!declaredWidth) return declaredCount || 1;

    const fittingCount = Math.max(1, Math.floor((availableWidth + columnGap) / (declaredWidth + columnGap)));
    return declaredCount ? Math.min(declaredCount, fittingCount) : fittingCount;
}

/**
 * Measures the horizontal text-column geometry of a chapter document, in the
 * document's own viewport coordinates. Each column exposes the right edge of
 * the text block it contains (`textRight`) — accounting for the body's
 * centering, line-length cap, gutter padding, and the CSS zoom Readium applies
 * for font sizing — plus the right limit available for docking beside it
 * (`boundRight`): where the next column's text starts, or Infinity when there
 * is no column to the right and the reader's own margin can be used.
 * @param {Window} frameWindow - The chapter iframe's contentWindow
 * @returns {{columnStride: number, columns: Array<{boundRight: number, textRight: number}>}|null}
 *   The geometry, or null when the document cannot be measured
 */
export function measureTextColumnGeometry(frameWindow) {
    const documentElement = frameWindow.document?.documentElement;
    const body = frameWindow.document?.body;
    if (!documentElement || !body) return null;

    const bodyStyle = frameWindow.getComputedStyle(body);
    const bodyZoom = parseFloat(bodyStyle.zoom) || 1;
    const paddingLeft = (parseFloat(bodyStyle.paddingLeft) || 0) * bodyZoom;
    const paddingRight = (parseFloat(bodyStyle.paddingRight) || 0) * bodyZoom;

    if (isScrollModeDocument(documentElement)) {
        const bodyRect = body.getBoundingClientRect();
        return {
            columnStride: Infinity,
            columns: [
                {
                    boundRight: Infinity,
                    textRight: bodyRect.right - paddingRight,
                },
            ],
        };
    }

    const availableWidth = documentElement.clientWidth;
    if (!availableWidth) return null;

    const rootStyle = frameWindow.getComputedStyle(documentElement);
    const columnGap = parseFloat(rootStyle.columnGap) || 0;
    const columnCount = resolveUsedColumnCount(rootStyle, availableWidth, columnGap);
    const columnWidth = (availableWidth - (columnCount - 1) * columnGap) / columnCount;
    const maxBodyWidth = (parseFloat(bodyStyle.maxWidth) || Infinity) * bodyZoom;
    const bodyWidth = Math.min(columnWidth, maxBodyWidth);

    const columns = [];
    for (let columnIndex = 0; columnIndex < columnCount; columnIndex++) {
        const columnLeft = columnIndex * (columnWidth + columnGap);
        const bodyLeft = columnLeft + (columnWidth - bodyWidth) / 2;
        columns.push({
            textLeft: bodyLeft + paddingLeft,
            textRight: bodyLeft + bodyWidth - paddingRight,
        });
    }
    return {
        columnStride: columnWidth + columnGap,
        columns: columns.map((column, columnIndex) => ({
            boundRight: columns[columnIndex + 1]?.textLeft ?? Infinity,
            textRight: column.textRight,
        })),
    };
}

/**
 * Picks the column that contains a rect, by the rect's horizontal center.
 * @param {{columnStride: number, columns: Array<Object>}|null} geometry - Measured geometry
 * @param {{left: number, right: number}} rect - Rect in the same viewport coordinates
 * @returns {{boundRight: number, textRight: number}|null}
 */
export function findColumnTextEdges(geometry, rect) {
    if (!geometry?.columns?.length) return null;

    const rectCenter = (rect.left + rect.right) / 2;
    const columnIndex = Math.floor(rectCenter / geometry.columnStride);
    const clampedIndex = Math.min(geometry.columns.length - 1, Math.max(0, columnIndex));
    return geometry.columns[clampedIndex];
}

/**
 * Resolves the per-document measurements needed to anchor markers — iframe rect,
 * visible band and column geometry — caching them so each chapter document is
 * read once per collection pass instead of once per highlight.
 * @param {Document} contentDocument - Chapter document that owns the marks
 * @param {Map<Document, Object|null>} cache - Per-pass measurement cache
 * @returns {{iframeRect: DOMRect, visibleBand: {left: number, right: number, top: number, bottom: number}, geometry: Object|null}|null}
 */
function resolveMarkerDocumentContext(contentDocument, cache) {
    if (cache.has(contentDocument)) return cache.get(contentDocument);

    const frameWindow = contentDocument.defaultView;
    const iframe = frameWindow?.frameElement;
    const context = iframe
        ? {
              iframeRect: iframe.getBoundingClientRect(),
              visibleBand: { left: 0, right: frameWindow.innerWidth, top: 0, bottom: frameWindow.innerHeight },
              geometry: measureTextColumnGeometry(frameWindow),
          }
        : null;
    cache.set(contentDocument, context);
    return context;
}

/**
 * Picks a mark's anchor rect per column it occupies in a paged multi-column
 * spread: among the fragments that are horizontally visible, the top-most one in
 * each column the mark spans, so a highlight seen across both columns gets a
 * marker in each. Fragments belonging to a column beyond the current spread —
 * the continuation on the next page, which the horizontal band can let through
 * by a rounding margin — are discarded so no marker docks on the next page.
 * Falls back to a single `findVisibleAnchorRect` when there is no measured
 * multi-column geometry (scroll mode or a single column). Each anchor is paired
 * with its resolved column edges so callers never re-derive the column.
 * @param {Array<{left: number, right: number, top: number}>} rects - Client rects of the mark fragments
 * @param {{left: number, right: number}} visibleBand - Horizontal bounds of the visible reading area
 * @param {{columnStride: number, columns: Array}|null} geometry - Measured column geometry
 * @returns {Array<{rect: {left: number, right: number, top: number}, column: {textRight: number, boundRight: number}|null}>} One anchor per occupied column, empty when none anchorable
 */
function collectColumnAnchors(rects, visibleBand, geometry) {
    if (!geometry || !Number.isFinite(geometry.columnStride) || geometry.columns.length < 2) {
        const anchor = findVisibleAnchorRect(rects, visibleBand);
        return anchor ? [{ rect: anchor, column: findColumnTextEdges(geometry, anchor) }] : [];
    }
    if (!rects?.length || !visibleBand) return [];

    const columnCount = geometry.columns.length;
    const topMostByColumn = new Map();
    for (const rect of rects) {
        if (rect.right <= visibleBand.left || rect.left >= visibleBand.right) continue;
        const columnIndex = Math.floor((rect.left + rect.right) / 2 / geometry.columnStride);
        if (columnIndex < 0 || columnIndex >= columnCount) continue;
        const currentTopMost = topMostByColumn.get(columnIndex);
        if (!currentTopMost || rect.top < currentTopMost.top) {
            topMostByColumn.set(columnIndex, rect);
        }
    }
    return Array.from(topMostByColumn, ([columnIndex, rect]) => ({
        rect,
        column: geometry.columns[columnIndex],
    }));
}

/**
 * Collects the note-marker anchor of every annotated highlight in the current
 * chapter: its visible mark rect plus the text edge of the column that contains
 * it, in the parent window's viewport coordinates (iframe offset already
 * applied). Highlights whose marks fall outside the visible band (e.g. another
 * column/page) yield no anchor. When the column geometry cannot be measured,
 * the anchor falls back to the iframe's right edge.
 * @param {Object<string, Object>} chapterHighlights - Current chapter highlights keyed by uuid
 * @returns {Array<{uuid: string, color: string, annotationText: string, top: number, columnTextRight: number, columnBoundRight: number}>}
 */
export function collectEpubNoteMarkerAnchors(chapterHighlights) {
    const anchors = [];
    const contextByDocument = new Map();

    for (const highlight of Object.values(chapterHighlights)) {
        if (!highlight.hasAnnotation || highlight.isFlashcard?.()) continue;

        try {
            const body = highlight.contextBodyDomNode;
            if (!body?.isConnected) continue;

            const marks = body.querySelectorAll(`.${highlight.getUniqueCssClass()}`);
            if (!marks.length) continue;

            const context = resolveMarkerDocumentContext(body.ownerDocument, contextByDocument);
            if (!context) continue;

            const markRects = Array.from(marks).flatMap(mark => Array.from(mark.getClientRects()));
            const columnAnchors = collectColumnAnchors(markRects, context.visibleBand, context.geometry);
            if (!columnAnchors.length) continue;

            const { iframeRect } = context;
            for (const { rect, column } of columnAnchors) {
                anchors.push({
                    uuid: highlight.uuid,
                    color: highlight.color,
                    annotationText: highlight.annotationText,
                    top: rect.top + iframeRect.top,
                    columnTextRight: column ? iframeRect.left + column.textRight : iframeRect.right,
                    columnBoundRight: column ? iframeRect.left + column.boundRight : Infinity,
                });
            }
        } catch {
            continue;
        }
    }
    return anchors;
}
